/**
 * Deterministic collision for the shared simulation (WR.2; AD-044/AD-045).
 *
 * This is the AD-026 single-implementation rule extended to collision: the
 * client predicts its own movement against EXACTLY this code, and the server
 * validates with it, so a player can never disagree with the server about
 * where a wall is (docs/architecture/world-rendering.md "Shared deterministic
 * collision"). Collision therefore obeys movement.ts's determinism rules:
 *
 * - Pure functions of their arguments: no clocks, no randomness, no I/O.
 * - Operations and iteration order are fixed, so client replay and server
 *   integration produce identical results from identical inputs.
 *
 * The world model is 2.5D (AD-044): gameplay is flat x/z. Collision resolves a
 * moving CIRCLE (the entity footprint) against a small set of static 2D SHAPES
 * (structure footprints) — never against render meshes. Terrain elevation is
 * sampled separately (`sampleHeight`) and is presentation-only at the slice:
 * it gives an entity its render `y` and is reserved for future walkability, but
 * it never changes x/z movement (the flat-sim invariant).
 *
 * Heavy derived artifacts (full heightfields, navmesh, collision meshes) are
 * REFERENCED by hash, never inlined (AD-044); WR.2 ships only small authored
 * footprint polygons plus the sampling capability.
 */
/** A tiny skin so a resolved circle ends just clear of a surface, not touching. */
const SKIN = 1e-4;
function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
}
/** Closest point on segment a→b to p (clamped to the segment). */
function closestOnSegment(px, pz, ax, az, bx, bz) {
    const dx = bx - ax;
    const dz = bz - az;
    const len2 = dx * dx + dz * dz;
    if (len2 < 1e-12)
        return { x: ax, z: az };
    const t = clamp(((px - ax) * dx + (pz - az) * dz) / len2, 0, 1);
    return { x: ax + dx * t, z: az + dz * t };
}
function pushOutOfCircle(cx, cz, r, s) {
    const dx = cx - s.x;
    const dz = cz - s.z;
    const minDist = r + s.radius;
    const dist = Math.hypot(dx, dz);
    if (dist >= minDist)
        return null;
    if (dist < 1e-9) {
        // Coincident centres: eject along +x deterministically.
        return { x: s.x + minDist + SKIN, z: s.z };
    }
    const scale = (minDist + SKIN) / dist;
    return { x: s.x + dx * scale, z: s.z + dz * scale };
}
function pushOutOfAabb(cx, cz, r, s) {
    const inside = cx >= s.minX && cx <= s.maxX && cz >= s.minZ && cz <= s.maxZ;
    if (!inside) {
        const qx = clamp(cx, s.minX, s.maxX);
        const qz = clamp(cz, s.minZ, s.maxZ);
        const dx = cx - qx;
        const dz = cz - qz;
        const dist = Math.hypot(dx, dz);
        if (dist >= r)
            return null;
        if (dist < 1e-9) {
            // On an edge: push out along +x deterministically.
            return { x: qx + r + SKIN, z: qz };
        }
        const scale = (r + SKIN) / dist;
        return { x: qx + dx * scale, z: qz + dz * scale };
    }
    // Centre inside the box: eject along the axis of least penetration.
    const dLeft = cx - s.minX;
    const dRight = s.maxX - cx;
    const dTop = cz - s.minZ;
    const dBottom = s.maxZ - cz;
    let min = dLeft;
    let axis = 0; // 0=left 1=right 2=top 3=bottom
    if (dRight < min) {
        min = dRight;
        axis = 1;
    }
    if (dTop < min) {
        min = dTop;
        axis = 2;
    }
    if (dBottom < min) {
        axis = 3;
    }
    switch (axis) {
        case 0:
            return { x: s.minX - r - SKIN, z: cz };
        case 1:
            return { x: s.maxX + r + SKIN, z: cz };
        case 2:
            return { x: cx, z: s.minZ - r - SKIN };
        default:
            return { x: cx, z: s.maxZ + r + SKIN };
    }
}
function pushOutOfPoly(cx, cz, r, s) {
    const pts = s.points;
    const n = pts.length;
    if (n < 3)
        return null;
    let bestX = 0;
    let bestZ = 0;
    let bestDist = Number.POSITIVE_INFINITY;
    let allPos = true;
    let allNeg = true;
    for (let i = 0; i < n; i += 1) {
        const a = pts[i];
        const b = pts[(i + 1) % n];
        // Sign of the cross product places the centre left/right of this edge;
        // for a convex polygon all signs agree iff the centre is inside.
        const cross = (b.x - a.x) * (cz - a.z) - (b.z - a.z) * (cx - a.x);
        if (cross < 0)
            allPos = false;
        if (cross > 0)
            allNeg = false;
        const cp = closestOnSegment(cx, cz, a.x, a.z, b.x, b.z);
        const d = Math.hypot(cx - cp.x, cz - cp.z);
        if (d < bestDist) {
            bestDist = d;
            bestX = cp.x;
            bestZ = cp.z;
        }
    }
    const inside = allPos || allNeg;
    if (!inside && bestDist >= r)
        return null;
    if (bestDist < 1e-9) {
        // Centre exactly on the boundary: nudge out along +x deterministically.
        return { x: bestX + r + SKIN, z: bestZ };
    }
    if (inside) {
        // Eject to just outside the nearest edge (best is on the boundary).
        const nx = (bestX - cx) / bestDist;
        const nz = (bestZ - cz) / bestDist;
        return { x: bestX + nx * (r + SKIN), z: bestZ + nz * (r + SKIN) };
    }
    // Outside but overlapping: push out along (centre − boundary) to r.
    const dx = cx - bestX;
    const dz = cz - bestZ;
    const scale = (r + SKIN) / bestDist;
    return { x: bestX + dx * scale, z: bestZ + dz * scale };
}
/**
 * Push a circle (cx, cz, r) out of one shape if they overlap; return the
 * corrected centre, or null when there is no overlap. Deterministic: a
 * coincident/degenerate case resolves along +x so both sides agree.
 */
export function pushOutOfShape(cx, cz, r, shape) {
    switch (shape.kind) {
        case 'circle':
            return pushOutOfCircle(cx, cz, r, shape);
        case 'aabb':
            return pushOutOfAabb(cx, cz, r, shape);
        case 'poly':
            return pushOutOfPoly(cx, cz, r, shape);
        default:
            return null;
    }
}
/**
 * Resolve a circle of radius `r` at (x, z) against all static `shapes`.
 *
 * Shapes are processed in array order; `passes` relaxation sweeps let a circle
 * wedged in a corner settle against two shapes (pushing out of one can push
 * into another). The pass count is fixed so the result is bounded and identical
 * on client and server. The early break on a clean sweep is also deterministic
 * (identical inputs ⇒ identical break point).
 */
export function resolveCircle(x, z, r, shapes, passes) {
    let cx = x;
    let cz = z;
    for (let pass = 0; pass < passes; pass += 1) {
        let moved = false;
        for (const shape of shapes) {
            const corrected = pushOutOfShape(cx, cz, r, shape);
            if (corrected) {
                cx = corrected.x;
                cz = corrected.z;
                moved = true;
            }
        }
        if (!moved)
            break;
    }
    return { x: cx, z: cz };
}
/**
 * Sample terrain height at (x, z) with bilinear interpolation, clamped at the
 * grid edges. Pure and deterministic. Returns 0 for a degenerate field. This
 * gives an entity its render `y`; it does not affect x/z movement at the slice
 * (the flat-sim invariant, AD-044).
 */
export function sampleHeight(hf, x, z) {
    if (hf.cols < 1 || hf.rows < 1 || hf.cellSize <= 0)
        return 0;
    const gx = clamp((x - hf.originX) / hf.cellSize, 0, hf.cols - 1);
    const gz = clamp((z - hf.originZ) / hf.cellSize, 0, hf.rows - 1);
    const i0 = Math.floor(gx);
    const j0 = Math.floor(gz);
    const i1 = Math.min(i0 + 1, hf.cols - 1);
    const j1 = Math.min(j0 + 1, hf.rows - 1);
    const fx = gx - i0;
    const fz = gz - j0;
    const h00 = hf.heights[j0 * hf.cols + i0] ?? 0;
    const h10 = hf.heights[j0 * hf.cols + i1] ?? 0;
    const h01 = hf.heights[j1 * hf.cols + i0] ?? 0;
    const h11 = hf.heights[j1 * hf.cols + i1] ?? 0;
    const top = h00 + (h10 - h00) * fx;
    const bottom = h01 + (h11 - h01) * fx;
    return top + (bottom - top) * fz;
}
//# sourceMappingURL=collision.js.map